import React, { memo, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import 'moment/locale/da';
import moment from 'moment';
import dynamic from 'next/dynamic';
import AppHeader from '@components/Layout/AppHeader';
import SideMenu from '@components/Menu/SideMenu/SideMenu';
import LoginScreen from '@components/Pages/LoginScreen';
import { useAuthentication } from '@providers/AuthProvider';
import { Content, Layout } from './AppLayout.styled';
import { useRouter } from 'next/router';
import { useStore } from '@store/appStore';
import useGrid from '@hooks/useGrid';
import Head from 'next/head';
import DefaultLoader from '@components/Loaders/DefaultLoader';
import { useI18n } from '@i18n/useI18n';
import { useWindowSize } from 'react-use';
import { breakpoints } from '@styles/tokens';
import { useStyleStore } from '@store/styleStore';
import Filter from '@components/Menu/Filter';
import useNavigatorOnline from 'use-navigator-online';
import persistentStore from '@store/persistentStore';
import { RegistrationViewType } from '@models/RegistrationViewType';
import SessionExpiredPrompt from '@components/Utils/SessionExpiredPrompt/SessionExpiredPrompt';
import NewVersionPrompt from '@components/Utils/NewVersionPrompt/NewVersionPrompt';
import useVersionCheck from '@hooks/useVersionCheck';
import useDebugTools from '@hooks/useDebugTools';

// Each of these only ever renders for one specific role/pathname at a time (see the JSX below),
// but a plain import still bundles all 5 into _app for every visitor regardless of their role -
// loading them on demand instead keeps a student from downloading CounselorNav/TeacherFloatingNav
// code they can never reach, etc.
const RegistrationControls = dynamic(() => import('@components/Registration/RegistrationControls'), { ssr: false });
const CounselorControls = dynamic(() => import('@components/Counselor/CounselorControls'), { ssr: false });
const CounselorPageControls = dynamic(() => import('@components/CounselorPage/CounselorPageControls'), { ssr: false });
const AdminControls = dynamic(() => import('@components/Admin/AdminControls'), { ssr: false });
const TeacherControls = dynamic(() => import('@components/Teacher/TeacherControls'), { ssr: false });

// Same reasoning - each only renders in a specific, infrequent state (an error/login screen, a
// popup, a QR scan in progress, a mobile-only settings panel...). This is also where most of the
// app-wide Lottie animation weight (the player itself plus several multi-hundred-KB animation
// JSONs) was getting pulled into _app's eager bundle from - ErrorPage alone brings its own.
const ErrorPage = dynamic(() => import('@components/Pages/ErrorPage'), { ssr: false });
const MobileRegistrationSettings = dynamic(() => import('@components/Registration/MobileRegistrationSettings'), { ssr: false });
const Tutorial = dynamic(() => import('@components/Tutorial/Tutorial'), { ssr: false });
const CompletionScreen = dynamic(() => import('@components/Registration/CompletionScreen'), { ssr: false });
const QRScanScreen = dynamic(() => import('@components/SQScan'), { ssr: false });
const Note = dynamic(() => import('@components/Note'), { ssr: false });
const FullPageDisable = dynamic(() => import('@components/Loaders/FullPageDisable'), { ssr: false });
const AlertPopup = dynamic(() => import('@components/Profile/AlertPopup'), { ssr: false });
const MultiLoginPage = dynamic(() => import('@components/Pages/MultiLoginPage'), { ssr: false });

const getMobileDetect = async (userAgent: NavigatorID['userAgent']) => {
  // console.log("")
};

interface AppLayoutProps {
  children: ReactNode;
}

const AppLayout = ({ children }: AppLayoutProps) => {
  const { user, error } = useAuthentication();
  const { registrationView } = persistentStore();
  const { pathname } = useRouter();
  const i18n = useI18n();
  const { width } = useWindowSize();
  const { floatingNavHeight } = useStyleStore();
  const { defaultQr } = persistentStore();
  const {
    client,
    settings,
    tutorial,
    note,
    teams,
    QRScreen,
    setQRScreen,
    mobileLogin,
    students,
    alertInfo,
    screenType,
    multiLogin,
    otherUser,
    schools,
    groups,
    clientSettings
  } = useStore();
  const [t, setT] = useState(false);
  const keepAliveRef = useRef(null);
  const infoBubble = useRef<Array<ReactElement>>([]);
  const { isOnline, isOffline, backOnline, backOffline } = useNavigatorOnline();
  const [status, setStatus] = useState(null);
  useGrid();
  useVersionCheck();
  useDebugTools();
  moment.locale(i18n.language);

  useEffect(() => {
    getMobileDetect(navigator.userAgent);
    // Cleans up a service worker registered by an older mobile build (before
    // next-pwa was disabled for NEXT_PUBLIC_IS_MOBILE builds) - Capacitor
    // serves the app locally, so a leftover SW here can only serve stale
    // content. Mobile-only: unregistering unconditionally would strip
    // offline support from the desktop PWA.
    if (process.env.NEXT_PUBLIC_IS_MOBILE === 'true' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        for (let registration of registrations) {
          registration.unregister();
        }
        if (registrations.length > 0) window.location.reload();
      });
    }
  }, []);

  // viewport height forcing
  useEffect(() => {
    if (isOnline) setStatus(true);
    const onResize = () => document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (backOnline) setStatus(true);
  }, [backOnline]);

  useEffect(() => {
    if (backOffline) setStatus(false);
  }, [backOffline]);
  

  const getComponent = () => {
    if (status === false) {
      return <ErrorPage errorCode={204} errorText={'Internet is required for Attender app'} errorDescription={'No internet!'} />;
    } else if (mobileLogin === 1) {
      return <ErrorPage errorCode={200} errorText={'login-screen-title'} errorDescription={'login-screen-description'} />;
    }
    else if (schools && otherUser) {
      return <MultiLoginPage errorCode={200} errorText={'login-screen-title'} errorDescription={'login-screen-description'} schools={schools} />;
    } else if (multiLogin === 1) {
      return <MultiLoginPage errorCode={200} errorText={'login-screen-title'} errorDescription={'login-screen-description'} />;
    } else {
      return <ErrorPage errorCode={error.code} errorText={error.title} errorDescription={error.message} />;
    }
  };

  return (
    <>
      <Head>
        <title>Attender</title>
      </Head>
      <Layout>
        {alertInfo.open && <AlertPopup />}
        <SessionExpiredPrompt />
        <NewVersionPrompt />
        {QRScreen && <QRScanScreen closeScreen={setQRScreen} />}

        {screenType.show && <CompletionScreen />}
        
        {tutorial ? <Tutorial open={tutorial} /> : ''}
        {note && <Note />}
        {students.loading && ((pathname === '/' && !groups?.includes(client?.SchoolLeaderGroupName)) || pathname === '/registration') && registrationView === RegistrationViewType.FULL_DAY && <FullPageDisable />}

        {infoBubble.current !== null && infoBubble.current !== undefined && infoBubble.current.length > 0
          ? infoBubble.current.map((ib) => ib)
          : null}

        {pathname == '/teamExtension' || pathname == '/teamExtension/meeting' || pathname == '/teamExtension/remove' || multiLogin=== 1 || (otherUser && schools) ? '' : <AppHeader />}

        {error || status === false ? null : (
          <>
            {user && client && ((pathname == '/' && !groups?.includes(client?.SchoolLeaderGroupName)) || pathname === '/registration' || (pathname == '/' && groups?.includes(client?.SchoolLeaderGroupName) && width < breakpoints.tablet) && (groups?.includes(client?.RegisterGroupName) || groups?.includes(client?.AdminGroupName) || groups?.includes(client?.GuestTeacherGroupName)))  && <RegistrationControls />}
            {user && client && pathname == '/' && <CounselorControls />}
            {user && client && (pathname == '/counselor' || pathname == '/caseworker') && <CounselorPageControls />}
            {user && client && (pathname == '/adminpage' || pathname == '/studentadmin' || pathname == '/writtenabsence' || pathname == '/merit' || pathname == '/caseworker') && <AdminControls />}
            {user && client && pathname == '/teacherpage' && <TeacherControls />}

            {width <= breakpoints.tablet && <MobileRegistrationSettings />}
          </>
        )}
        <Content inExtension={pathname == '/teamExtension' || pathname == '/teamExtension/meeting'} navHeight={floatingNavHeight}>
          {error || mobileLogin === 1 || multiLogin === 1 || status === false  || (otherUser && schools)? (
            getComponent()
          ) : (
            <>{user ? client && settings ? children : <DefaultLoader /> : <LoginScreen />}</>
          )}
        </Content>

        {<SideMenu />}
        {teams ? null : <Filter />}
      </Layout>
    </>
  );
};

export default memo(AppLayout);
